import React from "react";
import { useTranslation } from "react-i18next";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Slider,
  Typography,
  Box,
} from "@mui/material";
import { Trash2 } from "lucide-react";
import { ChairInput } from "../types";
import { RoleDef } from "../lib/roleSets";
import { SONIOX_VOICES } from "../lib/schemaModes";

interface ChairRowProps {
  chair: ChairInput;
  onChange: (next: ChairInput) => void;
  onRemove: () => void;
  gain: number;
  onGain: (v: number) => void;
  generated: boolean;
  roles: RoleDef[];
  roleI18nPrefix: string; // e.g. "experiential.modes" or "experiential.roles"
  roleLabelKey: string;   // e.g. "experiential.mode" or "experiential.part"
}

export function ChairRow({
  chair, onChange, onRemove, gain, onGain, generated, roles, roleI18nPrefix, roleLabelKey,
}: ChairRowProps) {
  const { t } = useTranslation();

  return (
    <div className="exp-chair-row">
      <FormControl size="small" sx={{ minWidth: 180 }}>
        <InputLabel>{t(roleLabelKey)}</InputLabel>
        <Select
          value={chair.mode}
          label={t(roleLabelKey)}
          onChange={(e) => onChange({ ...chair, mode: e.target.value })}
        >
          {roles.map((r) => (
            <MenuItem key={r.key} value={r.key}>
              {t(`${roleI18nPrefix}.${r.key}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        size="small"
        multiline
        minRows={2}
        label={t("experiential.text")}
        value={chair.text}
        onChange={(e) => onChange({ ...chair, text: e.target.value })}
        sx={{ flex: 1 }}
      />

      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel>{t("experiential.voice")}</InputLabel>
        <Select
          value={chair.voice}
          label={t("experiential.voice")}
          onChange={(e) => onChange({ ...chair, voice: e.target.value })}
        >
          {SONIOX_VOICES.map((v) => (
            <MenuItem key={v} value={v}>
              {v}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {generated && (
        <Box sx={{ width: 120 }}>
          <Typography variant="caption" color="text.secondary">
            {t("experiential.amplify")}
          </Typography>
          <Slider
            size="small"
            min={0}
            max={2}
            step={0.05}
            value={gain}
            onChange={(_e, v) => onGain(v as number)}
            aria-label={t("experiential.amplify")}
          />
        </Box>
      )}

      <button
        type="button"
        onClick={onRemove}
        aria-label={t("experiential.removeChair")}
        title={t("experiential.removeChair")}
        style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: "inherit" }}
      >
        <Trash2 size={16} />
      </button>
    </div>
  );
}
